/**
 * LoginHistoryTimeline — reusable timeline of sign-in events.
 *
 * Used from:
 *   • Profile page ("Recent sign-ins" section — user views their own)
 *   • AdminSessions drawer (super-admin drills into any user)
 *
 * Props:
 *   - endpoint:  `/auth/login-history` (self) or
 *                `/admin/users/{user_id}/login-history` (admin)
 *   - limit:     default 30
 *   - showTitle: draws the section header when true
 *   - onClose:   optional callback for the drawer usage
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, MonitorSmartphone, Chrome, Smartphone, KeyRound, Mail, RotateCw, ShieldAlert, CheckCircle2, XCircle, MapPin } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";

const METHOD_META = {
    password:   { label: "Password",  Icon: KeyRound,    tone: "text-sky-400" },
    otp:        { label: "OTP",       Icon: Mail,        tone: "text-indigo-400" },
    webauthn:   { label: "Passkey",   Icon: ShieldAlert, tone: "text-emerald-400" },
    refresh:    { label: "Refresh",   Icon: RotateCw,    tone: "text-muted-foreground" },
};

function OsIcon({ os }) {
    if (["iOS", "Android"].includes(os)) return <Smartphone className="w-3.5 h-3.5"/>;
    if (os === "macOS" || os === "Windows" || os === "Linux") return <MonitorSmartphone className="w-3.5 h-3.5"/>;
    return <Chrome className="w-3.5 h-3.5"/>;
}

export default function LoginHistoryTimeline({ endpoint, limit = 30, showTitle = true, testIdPrefix = "login-history" }) {
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(endpoint, { params: { limit } });
            setRows(data.rows || []);
        } catch (e) { toast.error(apiError(e)); setRows([]); }
        finally { setLoading(false); }
    }, [endpoint, limit]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid={testIdPrefix}>
            {showTitle && (
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-sm font-medium">Recent sign-ins</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Last {limit} sign-ins across all your devices. If you see one you don't recognise, change your password immediately.
                        </div>
                    </div>
                    <button onClick={load} data-testid={`${testIdPrefix}-refresh`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border border-border px-2.5 py-1.5 hover:border-primary">
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/> Refresh
                    </button>
                </div>
            )}
            {loading && rows === null ? (
                <div className="text-xs text-muted-foreground p-6 text-center">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-xs text-muted-foreground p-6 text-center">No sign-ins recorded yet.</div>
            ) : (
                <ol className="border-l border-border ml-2 space-y-4">
                    {rows.map(r => {
                        const m = METHOD_META[r.method] || METHOD_META.password;
                        const Icon = m.Icon;
                        return (
                            <li key={r.id} data-testid={`${testIdPrefix}-row-${r.id}`}
                                className={`relative pl-5 ${r.current_session ? "" : ""}`}>
                                <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 ${r.success ? "border-emerald-400 bg-background" : "border-rose-400 bg-background"} ${r.current_session ? "ring-2 ring-primary/60 ring-offset-2 ring-offset-background" : ""}`}/>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest ${m.tone}`}>
                                                <Icon className="w-3 h-3"/> {m.label}
                                            </span>
                                            {r.success
                                                ? <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400"><CheckCircle2 className="w-3 h-3"/> success</span>
                                                : <span className="inline-flex items-center gap-1 text-[10px] text-rose-400"><XCircle className="w-3 h-3"/> failed</span>}
                                            {r.current_session && (
                                                <span data-testid={`${testIdPrefix}-current`}
                                                    className="text-[10px] uppercase tracking-widest text-primary bg-primary/10 px-1.5 py-0.5">
                                                    this device
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                                            <OsIcon os={r.os}/>
                                            <span>{r.browser} on {r.os}</span>
                                            {(r.location || r.ip) && (
                                                <span className="text-muted-foreground inline-flex items-center gap-1">
                                                    <MapPin className="w-3 h-3"/>
                                                    {r.location
                                                        ? <>
                                                            <span>{r.location}</span>
                                                            <span className="font-mono text-[10px]">· {r.ip}</span>
                                                          </>
                                                        : <span className="font-mono">{r.ip}</span>}
                                                </span>
                                            )}
                                            {r.isp && (
                                                <span className="text-muted-foreground/70 text-[10px]" title={r.isp}>via {r.isp.length > 24 ? r.isp.slice(0, 22) + "…" : r.isp}</span>
                                            )}
                                            {r.device_fingerprint && (
                                                <span className="text-muted-foreground font-mono text-[10px]">fp:{r.device_fingerprint}</span>
                                            )}
                                        </div>
                                        {r.user_agent && (
                                            <div className="text-[10px] text-muted-foreground/70 mt-1 font-mono truncate" title={r.user_agent}>{r.user_agent}</div>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
                                        {fmtDateTime(r.created_at)}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
